import {
  Component, ElementRef, ViewChild, AfterViewInit,
  OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { BaseCellEditor } from './base-editor';
import { CELL_EDITOR_STYLES } from './cell-editor.styles';

const ALLOWED_EXTENSIONS =
  '.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.doc,.docx,.xls,.xlsx,.csv,.pdf,.zip,.kml,.kmz,.ppt,.pptx,.ics,.geojson';
const MAX_SIZE_MB = 100;
const EXTENSION_DISPLAY = 'PDF, DOC, XLS, PNG, JPG, GIF, CSV, ZIP, PPT, KML, GeoJSON';

import { ChildTableA11yService } from '../../services/child-table-a11y.service';
import { handleDragOver, handleDragLeave, handleDrop } from '../../core/file-utils';

@Component({
  selector: 'ct-file-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #modalBackdrop class="file-modal-backdrop"
      role="dialog" aria-modal="true"
      aria-labelledby="file-modal-title"
      (mousedown)="onBackdropClick($event)"
      (keydown)="onModalKeydown($event)">
      <div class="file-modal" (mousedown)="$event.stopPropagation()">

        <div class="file-modal-header">
          <span class="file-modal-title" id="file-modal-title">{{ column.label }}</span>
          <button type="button" class="file-modal-close" (click)="cancelModal()" aria-label="Close">
            <lucide-angular name="x" [size]="14" aria-hidden="true"></lucide-angular>
          </button>
        </div>

        <div #dropzone class="file-dropzone"
          [class.dragover]="isDragOver"
          [class.has-file]="!!pendingFile || !!fileName"
          tabindex="0" role="button"
          aria-label="Choose a file to upload. Press Enter or Space to browse."
          [attr.aria-describedby]="fileError ? 'file-error' : null"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          (click)="triggerFilePicker()"
          (keydown.enter)="triggerFilePicker(); $event.preventDefault()"
          (keydown.space)="triggerFilePicker(); $event.preventDefault()">
          <input #fileInput type="file" class="file-hidden-input"
            [accept]="acceptStr"
            (change)="onFileSelected($event)" />

          <ng-container *ngIf="!pendingFile && !fileName">
            <lucide-angular class="dropzone-icon" name="cloud-upload" [size]="40" aria-hidden="true"></lucide-angular>
            <span class="dropzone-label">Drag &amp; drop your file here</span>
            <span class="dropzone-sublabel">or click to browse</span>
            <span class="dropzone-hint">{{ extensionDisplay }}</span>
            <span class="dropzone-hint">Max {{ maxSizeMb }} MB</span>
          </ng-container>

          <ng-container *ngIf="pendingFile || fileName">
            <div class="file-selected-card" (click)="$event.stopPropagation()">
              <lucide-angular class="file-doc-icon" name="file-text" [size]="28" aria-hidden="true"></lucide-angular>
              <div class="file-info">
                <span class="file-info-name">{{ pendingFile?.name || fileName }}</span>
                <span *ngIf="pendingFile" class="file-info-size">{{ formatSize(pendingFile.size) }}</span>
                <span *ngIf="!pendingFile && fileName" class="file-info-existing">Current file</span>
              </div>
              <button type="button" class="file-remove-btn" (click)="removeFile($event)"
                aria-label="Remove file">
                <lucide-angular name="trash-2" [size]="16" aria-hidden="true"></lucide-angular>
              </button>
            </div>
            <span class="dropzone-sublabel replace-hint">Click the area above to replace</span>
          </ng-container>
        </div>

        <div *ngIf="fileError" id="file-error" class="file-error" role="alert">{{ fileError }}</div>

        <div class="file-modal-footer">
          <button type="button" class="file-modal-btn cancel" (click)="cancelModal()">Cancel</button>
          <button #saveBtn type="button" class="file-modal-btn save" (click)="saveModal()">Save</button>
        </div>
      </div>
    </div>
  `,
  styles: [CELL_EDITOR_STYLES, `
    :host { display: none; }
    .file-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 10000;
      background: rgba(0,0,0,0.45);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .file-modal {
      width: min(520px, 90vw);
      background: var(--og-surface-primary, #fff);
      border-radius: var(--og-radius-lg, 12px);
      box-shadow: 0 8px 32px rgba(0,0,0,0.24);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .file-modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      border-bottom: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .file-modal-title { font-weight: 600; font-size: 0.9375rem; }
    .file-modal-close {
      background: none; border: none; cursor: pointer; padding: 4px;
      color: var(--og-text-secondary, #666); border-radius: var(--og-radius-sm, 4px);
    }
    .file-modal-close:hover { background: var(--og-surface-secondary, #f5f5f5); }
    .file-dropzone {
      margin: 16px; padding: 28px 20px;
      border: 2px dashed var(--og-border-default, rgba(0,0,0,0.2));
      border-radius: var(--og-radius-md, 8px);
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      cursor: pointer; transition: border-color 0.15s, background 0.15s; text-align: center;
    }
    .file-dropzone:hover {
      border-color: var(--og-primary, #4b3fff); background: rgba(75, 63, 255, 0.03);
    }
    .file-dropzone.dragover {
      border-color: var(--og-primary, #4b3fff); background: rgba(75, 63, 255, 0.06);
    }
    .file-dropzone.has-file {
      border-style: solid; border-color: var(--og-border-default, rgba(0,0,0,0.12)); cursor: default;
    }
    .file-hidden-input { position: absolute; opacity: 0; width: 0; height: 0; overflow: hidden; }
    .dropzone-icon { color: var(--og-text-secondary, #666); margin-bottom: 4px; }
    .dropzone-label { font-size: 0.9375rem; font-weight: 500; color: var(--og-text-primary, #1a1a1a); }
    .dropzone-sublabel { font-size: 0.8125rem; color: var(--og-text-secondary, #666); }
    .dropzone-hint { font-size: 0.75rem; color: var(--og-text-disabled, #767676); }
    .replace-hint { margin-top: 8px; }
    .file-selected-card {
      display: flex; align-items: center; gap: 10px; padding: 10px 14px;
      background: var(--og-surface-secondary, #f7f7f8); border-radius: var(--og-radius-sm, 6px);
      width: 100%; max-width: 380px; box-sizing: border-box;
    }
    .file-doc-icon { color: var(--og-primary, #4b3fff); flex-shrink: 0; }
    .file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
    .file-info-name {
      font-size: 0.8125rem; font-weight: 500; color: var(--og-text-primary, #1a1a1a);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .file-info-size, .file-info-existing { font-size: 0.6875rem; color: var(--og-text-secondary, #666); }
    .file-remove-btn {
      background: none; border: none; cursor: pointer; padding: 4px;
      color: var(--og-text-secondary, #666); border-radius: var(--og-radius-sm, 4px); flex-shrink: 0;
    }
    .file-remove-btn:hover { color: var(--og-error, #d33423); background: rgba(211, 52, 35, 0.08); }
    .file-error {
      margin: 0 16px 8px; padding: 6px 10px; font-size: 0.8125rem;
      color: var(--og-error, #d33423); background: rgba(211, 52, 35, 0.06);
      border-radius: var(--og-radius-sm, 4px);
    }
    .file-modal-footer {
      display: flex; align-items: center; justify-content: flex-end; gap: 8px;
      padding: 10px 16px; border-top: 1px solid var(--og-border-default, rgba(0,0,0,0.12));
    }
    .file-modal-btn {
      padding: 6px 16px; border-radius: var(--og-radius-sm, 6px); font-size: 0.8125rem; font-weight: 500;
      cursor: pointer; border: 1px solid var(--og-border-default, rgba(0,0,0,0.2));
      background: var(--og-surface-primary, #fff); color: var(--og-text-primary, #1a1a1a);
    }
    .file-modal-btn:hover { background: var(--og-surface-secondary, #f5f5f5); }
    .file-modal-btn.save {
      background: var(--og-primary, #4b3fff); color: #fff; border-color: var(--og-primary, #4b3fff);
    }
    .file-modal-btn.save:hover { opacity: 0.9; }
    .file-modal-btn:focus-visible,
    .file-modal-close:focus-visible,
    .file-remove-btn:focus-visible {
      outline: 2px solid var(--og-primary, #4b3fff);
      outline-offset: 2px;
    }
  `],
})
export class FileEditorComponent extends BaseCellEditor implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('modalBackdrop') modalBackdropRef!: ElementRef<HTMLElement>;
  @ViewChild('saveBtn') saveBtnRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('dropzone') dropzoneRef!: ElementRef<HTMLElement>;

  fileName = '';
  pendingFile: { name: string; base64: string; size: number } | null = null;
  isDragOver = false;
  fileError = '';
  private pickerOpen = false;
  private portalEl: HTMLElement | null = null;

  readonly acceptStr = ALLOWED_EXTENSIONS;
  readonly extensionDisplay = EXTENSION_DISPLAY;
  readonly maxSizeMb = MAX_SIZE_MB;

  private readonly allowedExtSet = new Set(
    ALLOWED_EXTENSIONS.split(',').map((e) => e.trim().toLowerCase()),
  );

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly a11y: ChildTableA11yService
  ) { super(); }

  ngOnInit(): void {
    this.editingValue = this.value ?? '';
    if (typeof this.value === 'string' && this.value) {
      this.fileName = this.value;
    } else if (this.value?.name) {
      this.fileName = this.value.name;
      this.pendingFile = this.value;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.modalBackdropRef?.nativeElement;
      if (el) {
        document.body.appendChild(el);
        this.portalEl = el;
        const target = (!this.fileName && !this.pendingFile)
          ? this.dropzoneRef?.nativeElement
          : this.saveBtnRef?.nativeElement;
        target?.focus();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.portalEl?.parentNode === document.body) {
      document.body.removeChild(this.portalEl);
    }
  }

  // -- Drag and drop --

  onDragOver(event: DragEvent): void {
    handleDragOver(event);
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    handleDragLeave(event);
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    const file = handleDrop(event);
    this.isDragOver = false;
    if (file) this.processFile(file);
  }

  // -- File picker --

  triggerFilePicker(): void {
    if (this.pickerOpen) return;
    this.pickerOpen = true;
    this.fileInput?.nativeElement?.click();
    setTimeout(() => { this.pickerOpen = false; }, 1000);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.processFile(file);
    input.value = '';
  }

  // -- File processing --

  private processFile(file: File): void {
    this.fileError = '';
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    if (!this.allowedExtSet.has(ext)) {
      this.fileError = `File type "${ext}" is not allowed.`;
      this.cdr.detectChanges();
      return;
    }
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_SIZE_MB) {
      this.fileError = `File is too large (${sizeMb.toFixed(1)} MB). Maximum is ${MAX_SIZE_MB} MB.`;
      this.cdr.detectChanges();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.pendingFile = { name: file.name, base64: reader.result as string, size: file.size };
      this.editingValue = this.pendingFile;
      this.fileName = '';
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.pendingFile = null;
    this.fileName = '';
    this.editingValue = '';
    this.fileError = '';
    this.cdr.detectChanges();
  }

  // -- Modal actions --

  saveModal(): void { this.save.emit(this.editingValue); }
  cancelModal(): void { this.cancel.emit(); }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('file-modal-backdrop')) {
      this.saveModal();
    }
  }

  onModalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancelModal();
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (this.portalEl) {
      this.a11y.trapFocus(event, this.portalEl);
    }
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
